import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CheckCircle2, MapPin, Search, ShieldCheck } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { Badge } from "@/project/components/cards";
import { formatINR, initiatives } from "@/project/data/sample";
import { partners, partnerTypes, domainName } from "@/project/data/network";

export const Route = createFileRoute("/organisations/")({
  head: () => ({
    meta: [
      { title: "Partner Organisations — Daan Amrut Foundation" },
      { name: "description", content: "Browse verified panjrapols, gaushalas, NGOs and associated foundations raising funds on the Daan Amrut aggregator platform." },
      { property: "og:title", content: "Partner Organisations — Daan Amrut Foundation" },
      { property: "og:description", content: "Verified panjrapols, gaushalas, NGOs and foundations on the Daan Amrut network." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OrgDirectory,
});

function OrgDirectory() {
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("all");
  const [type, setType] = useState("all");
  const [only80G, setOnly80G] = useState(false);

  const list = useMemo(
    () =>
      partners.filter(
        (p) =>
          (domain === "all" || p.domain === domain) &&
          (type === "all" || p.type === type) &&
          (!only80G || p.has80G) &&
          (q === "" ||
            p.name.toLowerCase().includes(q.toLowerCase()) ||
            p.city.toLowerCase().includes(q.toLowerCase())),
      ),
    [q, domain, type, only80G],
  );

  return (
    <SiteShell>
      <section className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">Aggregator network</p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Partner organisations on the Daan Amrut platform
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground/70 sm:text-base">
            Panjrapols, gaushalas, NGOs and associated foundations — each verified, each with its own admin console,
            each raising under one trusted roof with instant 80G receipts.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/partner-apply" className="rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)]">
              Apply to join the network
            </Link>
            <Link to="/campaigns" className="rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold">
              Browse live campaigns
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid gap-3 rounded-2xl border border-border/60 bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
            <Search className="h-4 w-4 text-foreground/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or city" className="w-full bg-transparent text-sm outline-none" />
          </label>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
            <option value="all">All giving domains</option>
            {initiatives.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
            <option value="all">All organisation types</option>
            {partnerTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2 text-sm">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--success)]" /> 80G eligible only</span>
            <input type="checkbox" checked={only80G} onChange={(e) => setOnly80G(e.target.checked)} className="h-4 w-4 accent-[var(--maroon)]" />
          </label>
        </div>

        <p className="mt-6 text-sm text-foreground/60">{list.length} organisations</p>
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => (
            <Link
              key={p.id}
              to="/organisations/$id"
              params={{ id: p.id }}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={p.cover} alt={p.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                  {p.verified && <Badge tone="verified"><CheckCircle2 className="h-3 w-3" /> Verified</Badge>}
                  {p.has80G && <Badge tone="featured">80G</Badge>}
                </div>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-5">
                <p className="text-xs uppercase tracking-wider text-foreground/70">{p.type} · {domainName(p.domain)}</p>
                <h2 className="font-display text-lg font-semibold leading-snug">{p.name}</h2>
                <p className="flex items-center gap-1.5 text-xs text-foreground/60"><MapPin className="h-3.5 w-3.5" /> {p.city}, {p.state}</p>
                <p className="line-clamp-2 text-sm text-foreground/65">{p.about}</p>
                <div className="mt-auto flex items-center justify-between border-t border-border/60 pt-3 text-xs">
                  <span className="text-foreground/70">{p.liveCampaigns} live campaigns</span>
                  <span className="font-semibold text-[var(--maroon)]">{formatINR(p.raised)} raised</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
